<template>
  <div
    v-if="internalValue"
    class="draggable-dialog"
    :style="style"
    @mousedown="bringToFront"
  >
    <v-card class="draggable-card d-flex flex-column flex-grow-1" style="max-height: 100%; overflow: hidden;">
      <div class="draggable-header px-4 py-3" @mousedown="startDrag">
        <div class="d-flex justify-space-between align-center">
          <div class="d-flex flex-column">
            <span class="text-h6 font-weight-bold poppins-font">{{ title }}</span>
            <span v-if="subtitle" class="text-caption grey--text text--darken-1 poppins-font">{{ subtitle }}</span>
          </div>
          <v-btn icon small @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      
      <v-card-text class="pa-0 flex-grow-1" style="overflow: auto;">
        <slot></slot>
      </v-card-text>

      <div class="resize-handle" @mousedown.stop="startResize"></div>
    </v-card>
  </div>
</template>

<script>
import { nextZIndex } from '@/helpers/dialogZIndex';

export default {
  name: 'DraggableDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    initialWidth: {
      type: [Number, String],
      default: 800
    },
    initialHeight: {
      type: [Number, String],
      default: 600
    },
    maxHeight: {
      type: [Number, String],
      default: '90vh'
    },
    initialTop: {
      type: [Number, String],
      default: 100
    },
    initialLeft: {
      type: [Number, String],
      default: null // null means center
    }
  },
  data() {
    return {
      internalValue: this.value,
      width: parseInt(this.initialWidth),
      height: this.initialHeight === 'auto' ? 'auto' : parseInt(this.initialHeight),
      top: parseInt(this.initialTop),
      left: 0,
      zIndex: nextZIndex(),
      isDragging: false,
      isResizing: false,
      dragOffset: { x: 0, y: 0 }
    };
  },
  computed: {
    style() {
      return {
        width: `${this.width}px`,
        height: this.height === 'auto' ? 'auto' : `${this.height}px`,
        top: `${this.top}px`,
        left: `${this.left}px`,
        zIndex: this.zIndex,
        position: 'fixed',
        maxHeight: this.maxHeight, // Prevent it from being larger than viewport
        display: 'flex', // Ensure flex layout for children
        flexDirection: 'column'
      };
    }
  },
  watch: {
    value(val) {
      this.internalValue = val;
      if (val) {
        this.centerDialog();
      }
    },
    internalValue(val) {
      this.$emit('input', val);
    }
  },
  mounted() {
    if (this.internalValue) {
      this.centerDialog();
    }
  },
  methods: {
    close() {
      this.internalValue = false;
      this.$emit('close');
    },
    centerDialog() {
      // Adjust width if it exceeds window width
      if (this.width > window.innerWidth) {
        this.width = window.innerWidth - 40; // 20px padding each side
      }

      if (this.initialLeft !== null) {
        this.left = parseInt(this.initialLeft);
      } else {
        this.left = (window.innerWidth - this.width) / 2;
      }
      // Ensure it's on screen
      this.left = Math.max(0, this.left);
      this.top = Math.max(0, this.top);
      
      this.bringToFront();
    },
    bringToFront() {
      this.zIndex = nextZIndex();
    },
    startDrag(e) {
      this.bringToFront();
      this.isDragging = true;
      this.dragOffset.x = e.clientX - this.left;
      this.dragOffset.y = e.clientY - this.top;

      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      if (this.isDragging) {
        e.preventDefault();
        
        let newLeft = e.clientX - this.dragOffset.x;
        let newTop = e.clientY - this.dragOffset.y;

        // Boundary checks
        const maxLeft = window.innerWidth - this.width;
        // For height boundary, we need the actual offsetHeight if height is auto
        const currentHeight = this.$el ? this.$el.offsetHeight : (this.height === 'auto' ? 600 : this.height);
        const maxTop = window.innerHeight - currentHeight;

        newLeft = Math.max(0, Math.min(newLeft, maxLeft));
        newTop = Math.max(0, Math.min(newTop, maxTop));

        this.left = newLeft;
        this.top = newTop;
      }
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    startResize(e) {
      this.bringToFront();
      this.isResizing = true;
      window.addEventListener('mousemove', this.onResize);
      window.addEventListener('mouseup', this.stopResize);
      e.preventDefault();
    },
    onResize(e) {
      if (this.isResizing) {
        const newWidth = e.clientX - this.left;
        const newHeight = e.clientY - this.top;

        // Minimum dimensions
        if (newWidth > 300) this.width = newWidth;
        if (newHeight > 200) this.height = newHeight;
      }
    },
    stopResize() {
      this.isResizing = false;
      window.removeEventListener('mousemove', this.onResize);
      window.removeEventListener('mouseup', this.stopResize);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

.poppins-font {
  font-family: 'Poppins', sans-serif !important;
}

.draggable-dialog {
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  border-radius: 12px;
  background: white;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.draggable-header {
  cursor: move;
  background: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
  user-select: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: se-resize;
  background: radial-gradient(circle at bottom right, #ccc 10%, transparent 15%);
  z-index: 10;
}
</style>
